import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Avatar,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Grid,
  LinearProgress,
  Badge,
} from '@mui/material';
import {
  Person,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Security,
  Devices,
  Edit,
  PhoneAndroid,
  Key,
  History,
  Shield,
  Warning,
  CheckCircle,
  ContentCopy,
  QrCode2,
  Logout,
  DesktopWindows,
  TabletAndroid,
  Download,
} from '@mui/icons-material';
import api from '../../utils/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface Session {
  id: string;
  device: string;
  browser: string;
  location: string;
  lastActive: string;
  isCurrent: boolean;
  deviceType: 'desktop' | 'mobile' | 'tablet';
}

interface SecurityActivity {
  id: string;
  action: string;
  location: string;
  timestamp: string;
  status: 'success' | 'warning' | 'error';
}

const AccountSettings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Profile state
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');

  // Password change state
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorDialog, setTwoFactorDialog] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [totpQrUrl, setTotpQrUrl] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [disabling2FA, setDisabling2FA] = useState(false);
  const [disable2FAPassword, setDisable2FAPassword] = useState('');

  // Sessions
  const [sessions, setSessions] = useState<Session[]>([]);

  // Security activity
  const [securityActivity, setSecurityActivity] = useState<SecurityActivity[]>([]);

  // Load profile, 2FA status, sessions, and activity on mount
  const loadProfile = useCallback(async () => {
    try {
      const res = await api.get('/users/profile');
      const u = res.data?.data || res.data;
      setDisplayName(u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : (u.displayName || ''));
      setEmail(u.email || '');
      setPhone(u.phone || '');
      setAvatarUrl(u.avatarUrl || '');
    } catch (e) { console.error('Failed to load profile:', e); }
  }, []);

  const load2FAStatus = useCallback(async () => {
    try {
      const res = await api.get('/auth/2fa/status');
      const d = res.data?.data || res.data;
      setTwoFactorEnabled(d.enabled || d.twoFactorEnabled || false);
    } catch (e) { console.error('Failed to load 2FA status:', e); }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const res = await api.get('/users/sessions');
      const data = res.data?.data || res.data;
      const sessionList = Array.isArray(data) ? data : [];
      setSessions(sessionList.map((s: any) => ({
        id: s.id || s.sessionId || String(Math.random()),
        device: s.device || s.userAgent || 'Unknown Device',
        browser: s.browser || '',
        location: s.location || s.ip || 'Unknown',
        lastActive: s.lastActive || s.lastActiveAt || s.createdAt || 'Unknown',
        isCurrent: s.isCurrent || false,
        deviceType: (s.deviceType || 'desktop') as 'desktop' | 'mobile' | 'tablet',
      })));
    } catch (e) { console.error('Failed to load sessions:', e); }
  }, []);

  const loadActivity = useCallback(async () => {
    try {
      const res = await api.get('/users/activity?limit=20');
      const data = res.data?.data || res.data;
      const actList = Array.isArray(data) ? data : [];
      setSecurityActivity(actList.map((a: any) => ({
        id: a.id || String(Math.random()),
        action: a.action || a.description || a.type || 'Activity',
        location: a.location || a.ip || a.ipAddress || 'Unknown',
        timestamp: a.timestamp || a.createdAt || a.created_at || '',
        status: a.status === 'error' ? 'error' : a.status === 'warning' ? 'warning' : 'success' as 'success' | 'warning' | 'error',
      })));
    } catch (e) { console.error('Failed to load activity:', e); }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadProfile(), load2FAStatus(), loadSessions(), loadActivity()]);
      setLoading(false);
    };
    init();
  }, [loadProfile, load2FAStatus, loadSessions, loadActivity]);

  const calculatePasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 15;
    if (/[A-Z]/.test(password)) strength += 20;
    if (/[a-z]/.test(password)) strength += 10;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[^A-Za-z0-9]/.test(password)) strength += 15;
    return Math.min(strength, 100);
  };

  const passwordStrength = calculatePasswordStrength(newPassword);

  const getPasswordStrengthColor = (strength: number): 'error' | 'warning' | 'success' => {
    if (strength < 40) return 'error';
    if (strength < 70) return 'warning';
    return 'success';
  };

  const getPasswordStrengthText = (strength: number): string => {
    if (strength < 40) return 'Weak';
    if (strength < 70) return 'Medium';
    return 'Strong';
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const nameParts = displayName.trim().split(' ');
      await api.put('/users/profile', {
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        phone,
      });
      setSnackbar({ open: true, message: 'Profile saved successfully', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to save profile', severity: 'error' });
    }
    setIsSaving(false);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setSnackbar({ open: true, message: 'Passwords do not match', severity: 'error' });
      return;
    }
    if (passwordStrength < 40) {
      setSnackbar({ open: true, message: 'Password is too weak', severity: 'error' });
      return;
    }

    setIsSaving(true);
    try {
      await api.put('/users/password', { oldPassword: currentPassword, newPassword });
      setPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSnackbar({ open: true, message: 'Password changed successfully', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to change password', severity: 'error' });
    }
    setIsSaving(false);
  };

  const handleSetup2FA = async () => {
    setIsSaving(true);
    try {
      const res = await api.post('/auth/2fa/totp/setup');
      const d = res.data?.data || res.data;
      setTotpSecret(d.secret || d.manualEntry || '');
      setTotpQrUrl(d.qrCode || d.otpauthUrl || '');
      setTwoFactorDialog(true);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to setup 2FA', severity: 'error' });
    }
    setIsSaving(false);
  };

  const handleEnable2FA = async () => {
    setIsSaving(true);
    try {
      const res = await api.post('/auth/2fa/totp/verify', { token: twoFactorCode });
      const d = res.data?.data || res.data;
      setTwoFactorEnabled(true);
      if (d.backupCodes) setBackupCodes(d.backupCodes);
      setShowBackupCodes(d.backupCodes?.length > 0);
      setTwoFactorDialog(false);
      setTwoFactorCode('');
      setSnackbar({ open: true, message: 'Two-factor authentication enabled', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Invalid code. Please try again.', severity: 'error' });
    }
    setIsSaving(false);
  };

  const handleDisable2FA = async () => {
    if (!disable2FAPassword) {
      setSnackbar({ open: true, message: 'Password is required to disable 2FA', severity: 'error' });
      return;
    }
    setIsSaving(true);
    try {
      await api.post('/auth/2fa/disable', { password: disable2FAPassword });
      setTwoFactorEnabled(false);
      setDisabling2FA(false);
      setDisable2FAPassword('');
      setSnackbar({ open: true, message: 'Two-factor authentication disabled', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to disable 2FA', severity: 'error' });
    }
    setIsSaving(false);
  };

  const handleRevokeSession = async (sessionId: string) => {
    try {
      await api.delete(`/users/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      setSnackbar({ open: true, message: 'Session revoked', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to revoke session', severity: 'error' });
    }
  };

  const handleRevokeAllSessions = async () => {
    try {
      await api.delete('/users/sessions');
      setSessions(prev => prev.filter(s => s.isCurrent));
      setSnackbar({ open: true, message: 'All other sessions revoked', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to revoke sessions', severity: 'error' });
    }
  };

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'mobile': return <PhoneAndroid />;
      case 'tablet': return <TabletAndroid />;
      default: return <DesktopWindows />;
    }
  };

  return (
    <Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Typography variant="h5">Account Settings</Typography>
        {(isSaving || loading) && <CircularProgress size={16} color="primary" />}
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<Person />} label="Profile" iconPosition="start" />
        <Tab icon={<Security />} label="Security" iconPosition="start" />
        <Tab icon={<Devices />} label="Sessions" iconPosition="start" />
        <Tab icon={<History />} label="Activity" iconPosition="start" />
      </Tabs>

      {/* Profile Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 500 }}>
            Profile Photo
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <IconButton
                  size="small"
                  sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                  <Edit sx={{ fontSize: 14 }} />
                </IconButton>
              }
            >
              <Avatar
                src={avatarUrl}
                sx={{ width: 80, height: 80, fontSize: 32, bgcolor: 'primary.main' }}
              >
                {displayName.charAt(0)}
              </Avatar>
            </Badge>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                JPG, GIF or PNG. Max size 2MB.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" size="small" component="label">
                  Upload Photo
                  <input type="file" hidden accept="image/*" />
                </Button>
                {avatarUrl && (
                  <Button 
                    variant="outlined" 
                    size="small" 
                    color="error"
                    onClick={() => setAvatarUrl('')}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 500 }}>
            Personal Information
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Display Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Email Address"
                value={email}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Chip label="Primary" size="small" color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneAndroid color="action" />
                    </InputAdornment>
                  ),
                }}
                helperText="Used for account recovery and 2FA"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              onClick={handleSaveProfile}
              disabled={isSaving}
            >
              Save Changes
            </Button>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, borderColor: 'error.light' }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'error.main' }}>
            Danger Zone
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Delete Account</Typography>
              <Typography variant="caption" color="text.secondary">
                Permanently delete your account and all associated data
              </Typography>
            </Box>
            <Button variant="outlined" color="error">
              Delete Account
            </Button>
          </Box>
        </Paper>
      </TabPanel>

      {/* Security Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Lock color="primary" sx={{ fontSize: 28 }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Password
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last changed 30 days ago
                </Typography>
              </Box>
            </Box>
            <Button variant="outlined" onClick={() => setPasswordDialog(true)}>
              Change Password
            </Button>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: twoFactorEnabled ? 2 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Shield sx={{ color: twoFactorEnabled ? 'success.main' : 'text.secondary', fontSize: 28 }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Two-Factor Authentication
                  </Typography>
                  {twoFactorEnabled && <Chip label="Enabled" size="small" color="success" />}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Add an extra layer of security to your account
                </Typography>
              </Box>
            </Box>
            {!twoFactorEnabled ? (
              <Button variant="contained" color="success" onClick={handleSetup2FA} disabled={isSaving}>
                {isSaving ? <CircularProgress size={20} /> : 'Enable 2FA'}
              </Button>
            ) : (
              <Button variant="outlined" color="error" onClick={() => setDisabling2FA(true)}>
                Disable 2FA
              </Button>
            )}
          </Box>

          {twoFactorEnabled && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Your account is protected with two-factor authentication
              </Typography>
            </Alert>
          )}
        </Paper>

        {twoFactorEnabled && (
          <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Key sx={{ color: 'warning.main', fontSize: 28 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Backup Codes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use these codes if you lose access to your authenticator
                  </Typography>
                </Box>
              </Box>
              <Button variant="outlined" onClick={() => setShowBackupCodes(true)}>
                View Codes
              </Button>
            </Box>
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
            Security Recommendations
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Strong password"
                secondary="Your password meets security requirements"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                {twoFactorEnabled ? <CheckCircle color="success" /> : <Warning color="warning" />}
              </ListItemIcon>
              <ListItemText
                primary="Two-factor authentication"
                secondary={twoFactorEnabled ? '2FA is enabled' : 'Enable 2FA for better security'}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText
                primary="Recovery phone"
                secondary="Recovery phone number is set"
              />
            </ListItem>
          </List>
        </Paper>
      </TabPanel>

      {/* Sessions Tab */}
      <TabPanel value={tabValue} index={2}>
        <Alert severity="info" sx={{ mb: 3 }}>
          These are the devices currently logged into your account. You can revoke access to any device.
        </Alert>

        <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <List>
            {sessions.map((session, index) => (
              <React.Fragment key={session.id}>
                <ListItem sx={{ py: 2 }}
                  secondaryAction={
                    !session.isCurrent ? (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleRevokeSession(session.id)}
                        startIcon={<Logout />}
                      >
                        Revoke
                      </Button>
                    ) : undefined
                  }
                >
                  <ListItemIcon>
                    {getDeviceIcon(session.deviceType)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {session.device}
                        {session.isCurrent && (
                          <Chip label="This device" size="small" color="primary" />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        {session.location} • {session.lastActive}
                        <br />
                        <Typography variant="caption" color="text.secondary">
                          {session.browser}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                {index < sessions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>

        {sessions.length > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              color="error"
              onClick={handleRevokeAllSessions}
              startIcon={<Logout />}
            >
              Sign out of all other devices
            </Button>
          </Box>
        )}
      </TabPanel>

      {/* Activity Tab */}
      <TabPanel value={tabValue} index={3}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Recent security-related activity on your account
        </Alert>

        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <List>
            {securityActivity.map((activity, index) => (
              <React.Fragment key={activity.id}>
                <ListItem sx={{ py: 2 }}>
                  <ListItemIcon>
                    {activity.status === 'success' && <CheckCircle color="success" />}
                    {activity.status === 'warning' && <Warning color="warning" />}
                    {activity.status === 'error' && <Warning color="error" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={activity.action}
                    secondary={
                      <>
                        {activity.location} • {activity.timestamp}
                      </>
                    }
                  />
                </ListItem>
                {index < securityActivity.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button variant="outlined" startIcon={<Download />}>
            Download Activity Log
          </Button>
        </Box>
      </TabPanel>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              label="Current Password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {newPassword && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption">Password strength</Typography>
                  <Typography variant="caption" color={getPasswordStrengthColor(passwordStrength)}>
                    {getPasswordStrengthText(passwordStrength)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength}
                  color={getPasswordStrengthColor(passwordStrength)}
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            )}
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword !== '' && confirmPassword !== newPassword}
              helperText={confirmPassword !== '' && confirmPassword !== newPassword ? 'Passwords do not match' : ''}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={!currentPassword || !newPassword || !confirmPassword || isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2FA Setup Dialog */}
      <Dialog open={twoFactorDialog} onClose={() => setTwoFactorDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
            <Alert severity="info" sx={{ width: '100%' }}>
              Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.)
            </Alert>
            
            {totpQrUrl ? (
              <Box component="img" src={totpQrUrl} alt="QR Code" sx={{ width: 200, height: 200, borderRadius: 2 }} />
            ) : (
              <Box 
                sx={{ 
                  width: 200, 
                  height: 200, 
                  bgcolor: 'action.hover', 
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <QrCode2 sx={{ fontSize: 150, color: 'text.primary' }} />
              </Box>
            )}

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Can't scan? Enter this code manually:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                  {totpSecret || 'Loading...'}
                </Typography>
                <IconButton size="small" onClick={() => { navigator.clipboard.writeText(totpSecret); setSnackbar({ open: true, message: 'Copied!', severity: 'info' }); }}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Enter 6-digit code from app"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              inputProps={{ style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTwoFactorDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEnable2FA}
            disabled={twoFactorCode.length !== 6 || isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Verify & Enable'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={disabling2FA} onClose={() => setDisabling2FA(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
            Disabling 2FA will make your account less secure. Enter your password to confirm.
          </Alert>
          <TextField
            fullWidth
            label="Current Password"
            type="password"
            value={disable2FAPassword}
            onChange={(e) => setDisable2FAPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setDisabling2FA(false); setDisable2FAPassword(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisable2FA}
            disabled={!disable2FAPassword || isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onClose={() => setShowBackupCodes(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Backup Codes</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Save these codes in a safe place. Each code can only be used once.
          </Alert>
          
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
              {backupCodes.map((code, index) => (
                <Chip
                  key={index}
                  label={code}
                  sx={{ fontFamily: 'monospace' }}
                />
              ))}
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button startIcon={<Download />}>Download Codes</Button>
          <Button variant="contained" onClick={() => setShowBackupCodes(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountSettings;
